import { useState } from "react";

import CloseRounded from "@mui/icons-material/CloseRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import EventOutlined from "@mui/icons-material/EventOutlined";
import PersonOutlineOutlined from "@mui/icons-material/PersonOutlineOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import { Box, Button, Drawer, IconButton } from "@mui/material";
import { useSnackbar } from "notistack";
import { format } from "date-fns";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { closingCoverageApi } from "../../api/closingCoverageApi";
import {
  C,
  MONO,
  dateKeyInTz,
  entryLabel,
  entryTimeLabel,
  reasonStyle,
  tzAbbr,
  tzShortLabel,
} from "./coverageUtils";

const prettyDay = (iso, tz) => {
  const key = dateKeyInTz(iso, tz);
  const m = String(key || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), "EEE, MMM d, yyyy");
};

const Row = ({ icon: Icon, label, children }) => (
  <Flx g={1.25} sx={{ py: 1, alignItems: "flex-start" }}>
    <Flx
      center
      sx={{ width: 30, height: 30, borderRadius: "8px", background: C.lineSoft, color: C.sub, flexShrink: 0, mt: 0.25 }}
    >
      <Icon sx={{ fontSize: 15 }} />
    </Flx>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Txt sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.faint }}>
        {label}
      </Txt>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Box>
  </Flx>
);

const OOODetailDrawer = ({ entry, open, onClose, currentEmail, isAdmin, onEdit, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [deleting, setDeleting] = useState(false);

  const s = reasonStyle(entry?.reason);
  const tz = entry?.timezone;
  const canManage =
    !!entry &&
    (isAdmin ||
      (currentEmail &&
        String(entry.userEmail || "").toLowerCase() === currentEmail.toLowerCase()));

  const handleDelete = async () => {
    if (!entry?._id) return;
    setDeleting(true);
    try {
      await closingCoverageApi.deleteOoo(entry._id);
      enqueueSnackbar("Coverage entry removed.", { variant: "success" });
      onChanged?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not delete the entry.";
      enqueueSnackbar(String(detail), { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const sameDay =
    entry && dateKeyInTz(entry.start, tz) === dateKeyInTz(entry.end, tz);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: "min(400px, 100%)", background: C.surface } } }}
    >
      {entry && (
        <>
          <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${C.line}` }}>
            <Flx jb g={1} sx={{ alignItems: "flex-start" }}>
              <Box sx={{ minWidth: 0 }}>
                <Flx ac g={0.75} sx={{ mb: 0.5 }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: "50%", background: s.c }} />
                  <Txt sx={{ fontSize: 11.5, fontWeight: 700, color: s.c }}>
                    {entry.reason || "Out of office"}
                  </Txt>
                </Flx>
                <Txt sx={{ fontSize: 17, fontWeight: 800, color: C.ink }}>
                  {entryLabel(entry)}
                </Txt>
              </Box>
              <IconButton size="small" onClick={onClose} sx={{ color: C.faint }}>
                <CloseRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Flx>
          </Box>

          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2.5, py: 1 }}>
            <Row icon={EventOutlined} label="When">
              <Txt sx={{ fontSize: 13, fontWeight: 600, color: C.ink }}>
                {prettyDay(entry.start, tz)}
                {!sameDay ? ` – ${prettyDay(entry.end, tz)}` : ""}
              </Txt>
              <Txt sx={{ fontFamily: MONO, fontSize: 12, color: C.sub, mt: 0.25 }}>
                {entryTimeLabel(entry)}
                {tz ? ` · ${tzAbbr(entry.start, tz) || tzShortLabel(tz)}` : ""}
              </Txt>
            </Row>

            <Row icon={PersonOutlineOutlined} label="Person">
              <Txt sx={{ fontSize: 13, color: C.ink }}>{entry.userFullName || "—"}</Txt>
              {entry.userEmail && (
                <Txt sx={{ fontSize: 12, color: C.accent }}>{entry.userEmail}</Txt>
              )}
            </Row>

            <Row icon={ShieldOutlined} label="Backup">
              {entry.backupEmail ? (
                <>
                  <Txt sx={{ fontSize: 13, color: C.ink }}>
                    {entry.backupFullName || entry.backupEmail}
                  </Txt>
                  {entry.backupFullName && (
                    <Txt sx={{ fontSize: 12, color: C.accent }}>{entry.backupEmail}</Txt>
                  )}
                </>
              ) : (
                <Txt sx={{ fontSize: 13, color: C.faint }}>No backup assigned</Txt>
              )}
            </Row>

            {entry.note && (
              <Box sx={{ mt: 1, p: 1.25, borderRadius: "8px", background: C.bg, border: `1px solid ${C.line}` }}>
                <Txt sx={{ fontSize: 12.5, color: C.sub, whiteSpace: "pre-wrap" }}>
                  {entry.note}
                </Txt>
              </Box>
            )}
          </Box>

          {canManage && (
            <Flx
              ac
              g={1}
              sx={{ px: 2.5, py: 2, borderTop: `1px solid ${C.line}` }}
            >
              <Button
                fullWidth
                variant="outlined"
                color="gray"
                startIcon={<EditOutlined />}
                onClick={() => onEdit?.(entry)}
                sx={{ textTransform: "none", border: `1px solid ${C.line}` }}
              >
                Edit
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineRounded />}
                onClick={handleDelete}
                disabled={deleting}
                sx={{ textTransform: "none" }}
              >
                Delete
              </Button>
            </Flx>
          )}
        </>
      )}
    </Drawer>
  );
};

export default OOODetailDrawer;
